/**
 * In-memory conversation inbox for the mock API client (tracker item 56).
 * Kept out of mock.ts: MockAPIClient hands it a small host (the signed-in
 * user, the inbox setting, customers, jobs, users and the portal scope) and
 * delegates the IConversationsApi methods. Same rules as the backend where
 * the UI depends on them: the inboxAccess gate (403 INBOX_ACCESS_DENIED),
 * the opt-out / no-email / bounced 409s, statuses and closedAt, assignees
 * allowed by the setting, job of the customer, unread per user, the Unmatched
 * tab (attach, merge, discard), notes, portal threads with first-name staff.
 * Not modelled: real email, delivery webhooks, inbound routing (two sample
 * inbound emails are seeded per tenant instead).
 */
import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';
import * as types from './types';

function apiError(status: number, message: string, code?: string): AxiosError {
  const config = { headers: new AxiosHeaders() };
  const response = { status, statusText: '', headers: {}, config, data: { success: false, message, ...(code ? { code } : {}) } } as AxiosResponse;
  return new AxiosError(message, undefined, config, undefined, response);
}

const invalid = (message: string) => apiError(400, message, 'VALIDATION_ERROR');
const notFound = (message = 'Conversation not found') => apiError(404, message, 'NOT_FOUND');
const conflict = (message: string, code: string) => apiError(409, message, code);

export interface MockConversationUser {
  id: string;
  tenantId: string;
  email: string;
  role: string;
  isActive?: boolean;
  firstName?: string | null;
}

/** What the mock inbox reads in the rest of the mock. */
export interface MockConversationHost {
  /** The signed-in user (throws when signed out) */
  currentUser(): MockConversationUser;
  inboxAccess(tenantId: string): types.InboxAccess;
  customer(tenantId: string, id: string): { id: string; name: string; email?: string | null; notificationOptOut?: boolean; emailInvalid?: boolean } | undefined;
  /** Customers of the tenant (the seeded inbound mail picks from them) */
  customers(tenantId: string): { id: string; name: string; email?: string | null }[];
  job(tenantId: string, id: string): { id: string; customerId: string } | undefined;
  user(tenantId: string, id: string): MockConversationUser | undefined;
  /** A linked CUSTOMER_PORTAL login, else 403 */
  portalCustomer(): { tenantId: string; customerId: string; userId: string; email: string };
}

type Row = types.Conversation & { tenantId: string; portalThreadKey: string | null };
type MessageRow = types.ConversationMessage & { tenantId: string };

const INBOX_ROLES: Record<types.InboxAccess, string[]> = {
  STAFF: ['ADMIN', 'OFFICE_MANAGER'],
  EVERYONE: ['ADMIN', 'OFFICE_MANAGER', 'TECHNICIAN'],
};

let sequence = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(++sequence).toString(36)}`;

export class MockConversations {
  private rows: Row[] = [];
  private messages: MessageRow[] = [];
  /** `${conversationId}:${userId}` -> last read time */
  private reads = new Map<string, string>();
  private seeded = new Set<string>();
  private keys = new Map<string, string>();

  constructor(private readonly host: MockConversationHost) {}

  // ------------------------------------------------------------------ gate

  private staff(): MockConversationUser {
    const user = this.host.currentUser();
    if (!INBOX_ROLES[this.host.inboxAccess(user.tenantId)].includes(user.role)) {
      throw apiError(403, 'The inbox is not available to your role', 'INBOX_ACCESS_DENIED');
    }
    this.seed(user.tenantId);
    return user;
  }

  /** Two inbound emails per tenant so the inbox is not empty offline: one from a customer, one unmatched. */
  private seed(tenantId: string): void {
    if (this.seeded.has(tenantId)) return;
    this.seeded.add(tenantId);
    const customer = this.host.customers(tenantId).find((c) => c.email);
    const at = (minutesAgo: number) => new Date(Date.now() - minutesAgo * 60_000).toISOString();
    if (customer) {
      const row = this.newRow(tenantId, { customerId: customer.id, subject: 'Gate code for Friday', createdAt: at(90) });
      this.addInbound(row, customer.email as string, 'The gate code is 1234. Please close it behind you.\n\nOn Mon, the office wrote:\n> We will come Friday.', at(60));
    }
    const unmatched = this.newRow(tenantId, { customerId: null, subject: 'Quote for a hedge trim?', createdAt: at(30), status: 'UNMATCHED' });
    this.addInbound(unmatched, 'new.person@example.org', 'Hi, how much would you charge to trim a 20 m hedge?', at(30));
  }

  private newRow(tenantId: string, data: { customerId: string | null; subject: string; createdAt: string; status?: types.ConversationStatus; jobId?: string | null; createdBy?: string; portalUserId?: string }): Row {
    const customer = data.customerId ? this.host.customer(tenantId, data.customerId) : undefined;
    const row: Row = {
      id: nextId('conv'),
      tenantId,
      status: data.status ?? 'OPEN',
      subject: data.subject,
      customer: customer ? { id: customer.id, name: customer.name, email: customer.email ?? null } : null,
      customerOptedOut: !!customer?.notificationOptOut,
      jobId: data.jobId ?? null,
      invoiceId: null,
      estimateId: null,
      assignedUserId: null,
      assignee: null,
      lastMessageAt: data.createdAt,
      lastInboundAt: null,
      closedAt: null,
      createdAt: data.createdAt,
      updatedAt: data.createdAt,
      unread: false,
      origin: data.portalUserId ? 'PORTAL' : 'EMAIL',
      portalUserId: data.portalUserId ?? null,
      heldForConversationId: null,
      discardedAt: null,
      portalThreadKey: data.portalUserId && data.customerId ? `${data.portalUserId}:${data.customerId}` : null,
    };
    this.rows.push(row);
    return row;
  }

  private addInbound(row: Row, from: string, text: string, at: string, channel: types.ConversationChannel = 'EMAIL', portalUserId: string | null = null): MessageRow {
    const quoteAt = text.search(/\n\s*On .*wrote:/);
    const message: MessageRow = {
      id: nextId('msg'),
      tenantId: row.tenantId,
      conversationId: row.id,
      direction: 'INBOUND',
      channel,
      fromAddress: from,
      toAddress: null,
      bodyText: text,
      displayText: quoteAt > 0 ? text.slice(0, quoteAt).trim() : text,
      html: null,
      isUnsubscribeRequest: /^\s*(stop|unsubscribe)\s*[.!]*\s*$/i.test(text.split('\n')[0] ?? ''),
      attachments: null,
      authorUserId: null,
      authorName: null,
      portalUserId,
      isAutoReply: false,
      createdAt: at,
      delivery: null,
    };
    this.messages.push(message);
    row.lastInboundAt = at;
    row.lastMessageAt = at;
    if (row.status === 'CLOSED' || row.status === 'WAITING_ON_CUSTOMER') {
      row.status = 'OPEN';
      row.closedAt = null;
    }
    return message;
  }

  // ----------------------------------------------------------------- views

  private find(tenantId: string, id: string): Row {
    const row = this.rows.find((r) => r.id === id && r.tenantId === tenantId);
    if (!row) throw notFound();
    return row;
  }

  private lastRead(conversationId: string, userId: string): string | null {
    return this.reads.get(`${conversationId}:${userId}`) ?? null;
  }

  private view(row: Row, userId: string): types.Conversation {
    const { tenantId, portalThreadKey, ...rest } = row;
    const customer = row.customer ? this.host.customer(tenantId, row.customer.id) : undefined;
    const lastRead = this.lastRead(row.id, userId);
    const assignee = row.assignedUserId ? this.host.user(tenantId, row.assignedUserId) : undefined;
    return {
      ...rest,
      customer: customer ? { id: customer.id, name: customer.name, email: customer.email ?? null } : row.customer,
      customerOptedOut: !!customer?.notificationOptOut,
      assignee: row.assignedUserId ? { id: row.assignedUserId, name: assignee?.firstName || assignee?.email || '' } : null,
      unread: !!row.lastInboundAt && (!lastRead || row.lastInboundAt > lastRead),
    };
  }

  private messageView(m: MessageRow): types.ConversationMessage {
    const { tenantId, ...rest } = m;
    return { ...rest };
  }

  private bodyOf(raw: unknown, max = 10000): string {
    const body = typeof raw === 'string' ? raw.replace(/\r\n?/g, '\n').trim() : '';
    if (!body) throw invalid('body: is required');
    if (body.length > max) throw invalid(`body: at most ${max} characters`);
    return body;
  }

  // ------------------------------------------------------------------ staff

  list(query: types.ConversationListQuery = {}): types.ConversationPage {
    const user = this.staff();
    const page = query.page ?? 1;
    const limit = query.limit ?? 25;
    if (query.status && !(types.STAFF_CONVERSATION_STATUSES as readonly string[]).includes(query.status)) throw invalid('status: one of OPEN, WAITING_ON_CUSTOMER, CLOSED');
    const search = (query.search ?? '').trim().toLowerCase();
    const rows = this.rows
      .filter((r) => r.tenantId === user.tenantId && r.status !== 'UNMATCHED')
      .filter((r) => !query.status || r.status === query.status)
      .filter((r) => !query.assignee || (query.assignee === 'me' ? r.assignedUserId === user.id : query.assignee === 'none' ? !r.assignedUserId : r.assignedUserId === query.assignee))
      .filter((r) => !query.customerId || r.customer?.id === query.customerId)
      .filter((r) => !query.jobId || r.jobId === query.jobId)
      .map((r) => this.view(r, user.id))
      .filter((r) => !query.unread || r.unread)
      .filter((r) => !search || r.subject.toLowerCase().includes(search) || (r.customer?.name ?? '').toLowerCase().includes(search) || (r.customer?.email ?? '').toLowerCase().includes(search))
      .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
    return { data: rows.slice((page - 1) * limit, page * limit), pagination: { page, limit, total: rows.length, pages: Math.max(1, Math.ceil(rows.length / limit)) } };
  }

  unreadCount(): types.UnreadCounts {
    const user = this.staff();
    const unread = this.rows.filter((r) => r.tenantId === user.tenantId && r.status !== 'UNMATCHED').map((r) => this.view(r, user.id)).filter((r) => r.unread);
    return { total: unread.length, assignedToMe: unread.filter((r) => r.assignedUserId === user.id).length };
  }

  get(id: string): types.ConversationDetail {
    const user = this.staff();
    const row = this.find(user.tenantId, id);
    const messages = this.messages.filter((m) => m.conversationId === row.id).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return { conversation: this.view(row, user.id), messages: messages.map((m) => this.messageView(m)) };
  }

  create(data: types.CreateConversationInput): types.ConversationSendResult {
    const user = this.staff();
    const subject = (data.subject ?? '').replace(/\s+/g, ' ').trim();
    if (!data.customerId) throw invalid('customerId: is required');
    if (!subject) throw invalid('subject: is required');
    if (subject.length > 200) throw invalid('subject: at most 200 characters');
    const body = this.bodyOf(data.body);
    const customer = this.host.customer(user.tenantId, data.customerId);
    if (!customer) throw notFound('Customer not found');
    if (data.idempotencyKey) {
      const existing = this.keys.get(`${user.tenantId}:new:${data.idempotencyKey}`);
      if (existing) {
        const message = this.messages.find((m) => m.id === existing)!;
        return { conversation: this.view(this.find(user.tenantId, message.conversationId), user.id), message: this.messageView(message), deduplicated: true };
      }
    }
    if (customer.notificationOptOut) throw conflict('This customer opted out of emails; you can only answer them once they contact you', 'CUSTOMER_OPTED_OUT');
    if (!(customer.email ?? '').trim()) throw conflict('This customer has no email address', 'CUSTOMER_NO_EMAIL');
    if (customer.emailInvalid) throw conflict("This customer's email address bounced or reported spam; update it first", 'CUSTOMER_EMAIL_INVALID');
    if (data.jobId) this.requireJobOf(user.tenantId, data.jobId, customer.id);
    const row = this.newRow(user.tenantId, { customerId: customer.id, subject, createdAt: new Date().toISOString(), jobId: data.jobId ?? null, createdBy: user.id });
    const message = this.outbound(row, user, body);
    if (data.idempotencyKey) this.keys.set(`${user.tenantId}:new:${data.idempotencyKey}`, message.id);
    return { conversation: this.view(row, user.id), message: this.messageView(message), deduplicated: false };
  }

  reply(id: string, rawBody: string, idempotencyKey?: string): types.ConversationSendResult {
    const user = this.staff();
    const body = this.bodyOf(rawBody);
    const row = this.find(user.tenantId, id);
    if (row.status === 'UNMATCHED' || !row.customer) throw conflict('Attach this message to a customer before replying', 'CONVERSATION_UNMATCHED');
    if (idempotencyKey) {
      const existing = this.keys.get(`${row.id}:${idempotencyKey}`);
      if (existing) {
        const message = this.messages.find((m) => m.id === existing)!;
        return { conversation: this.view(row, user.id), message: this.messageView(message), deduplicated: true };
      }
    }
    const customer = this.host.customer(user.tenantId, row.customer.id);
    const wrote = this.messages.some((m) => m.conversationId === row.id && m.direction === 'INBOUND' && !m.isAutoReply);
    if (customer?.notificationOptOut && !wrote) throw conflict('This customer opted out of emails; you can only answer them once they contact you', 'CUSTOMER_OPTED_OUT');
    const message = this.outbound(row, user, body);
    if (idempotencyKey) this.keys.set(`${row.id}:${idempotencyKey}`, message.id);
    return { conversation: this.view(row, user.id), message: this.messageView(message), deduplicated: false };
  }

  private outbound(row: Row, user: MockConversationUser, body: string): MessageRow {
    const now = new Date().toISOString();
    const customer = row.customer ? this.host.customer(row.tenantId, row.customer.id) : undefined;
    const lastFrom = [...this.messages].reverse().find((m) => m.conversationId === row.id && m.direction === 'INBOUND' && m.channel === 'EMAIL')?.fromAddress;
    const to = row.portalUserId ? this.host.user(row.tenantId, row.portalUserId)?.email ?? null : lastFrom ?? customer?.email ?? null;
    const skip = !to ? 'NO_EMAIL' : customer?.emailInvalid ? 'EMAIL_INVALID' : null;
    const message: MessageRow = {
      id: nextId('msg'),
      tenantId: row.tenantId,
      conversationId: row.id,
      direction: 'OUTBOUND',
      channel: row.portalUserId ? 'PORTAL' : 'EMAIL',
      fromAddress: 'office@company.example',
      toAddress: to,
      bodyText: body,
      displayText: body,
      html: null,
      isUnsubscribeRequest: false,
      attachments: null,
      authorUserId: user.id,
      authorName: user.firstName || user.email,
      portalUserId: null,
      isAutoReply: false,
      createdAt: now,
      delivery: {
        outboxId: nextId('outbox'),
        status: skip ? 'SKIPPED' : 'PENDING',
        skipReason: skip,
        skipReasonText: skip === 'NO_EMAIL' ? 'The customer has no email address' : skip ? 'The email address bounced or reported spam' : null,
        deliveryStatus: null,
        sentAt: null,
      },
    };
    this.messages.push(message);
    Object.assign(row, { status: 'WAITING_ON_CUSTOMER', closedAt: null, lastMessageAt: now, updatedAt: now });
    this.reads.set(`${row.id}:${user.id}`, now);
    return message;
  }

  note(id: string, rawBody: string): types.ConversationMessage {
    const user = this.staff();
    const body = this.bodyOf(rawBody);
    const row = this.find(user.tenantId, id);
    const message: MessageRow = {
      id: nextId('msg'), tenantId: row.tenantId, conversationId: row.id, direction: 'NOTE', channel: 'INTERNAL', fromAddress: null, toAddress: null,
      bodyText: body, displayText: body, html: null, isUnsubscribeRequest: false, attachments: null, authorUserId: user.id,
      authorName: user.firstName || user.email, portalUserId: null, isAutoReply: false, createdAt: new Date().toISOString(), delivery: null,
    };
    this.messages.push(message);
    return this.messageView(message);
  }

  update(id: string, data: types.UpdateConversationInput): types.Conversation {
    const user = this.staff();
    const row = this.find(user.tenantId, id);
    if (Object.keys(data).length === 0) throw invalid('Nothing to change: send status, assignedUserId, jobId, customerId or subject');
    if (row.status === 'UNMATCHED') throw conflict('Attach this message to a customer first', 'CONVERSATION_UNMATCHED');
    if (data.status !== undefined && !(types.STAFF_CONVERSATION_STATUSES as readonly string[]).includes(data.status)) throw invalid('status: one of OPEN, WAITING_ON_CUSTOMER, CLOSED');
    if (data.assignedUserId) {
      const assignee = this.host.user(user.tenantId, data.assignedUserId);
      if (!assignee || assignee.isActive === false || !INBOX_ROLES[this.host.inboxAccess(user.tenantId)].includes(assignee.role)) {
        throw apiError(400, 'Assign the conversation to an active user who can see the inbox', 'INVALID_ASSIGNEE');
      }
    }
    let customerId = row.customer?.id ?? '';
    if (data.customerId && data.customerId !== customerId) {
      if (!this.host.customer(user.tenantId, data.customerId)) throw notFound('Customer not found');
      if (row.portalThreadKey) throw conflict('A portal thread stays with the customer of the portal login', 'PORTAL_THREAD_CUSTOMER');
      customerId = data.customerId;
      const customer = this.host.customer(user.tenantId, customerId)!;
      row.customer = { id: customer.id, name: customer.name, email: customer.email ?? null };
      row.jobId = null;
      row.invoiceId = null;
      row.estimateId = null;
    }
    if (data.jobId !== undefined) {
      if (data.jobId) this.requireJobOf(user.tenantId, data.jobId, customerId);
      row.jobId = data.jobId;
    }
    if (data.status !== undefined && data.status !== row.status) {
      row.status = data.status;
      row.closedAt = data.status === 'CLOSED' ? new Date().toISOString() : null;
    }
    if (data.assignedUserId !== undefined) row.assignedUserId = data.assignedUserId;
    if (data.subject !== undefined) {
      const subject = data.subject.replace(/\s+/g, ' ').trim();
      if (!subject) throw invalid('subject: is required');
      row.subject = subject;
    }
    row.updatedAt = new Date().toISOString();
    return this.view(row, user.id);
  }

  private requireJobOf(tenantId: string, jobId: string, customerId: string): void {
    const job = this.host.job(tenantId, jobId);
    if (!job) throw notFound('Job not found');
    if (job.customerId !== customerId) throw conflict('The job belongs to another customer', 'JOB_CUSTOMER_MISMATCH');
  }

  markRead(id: string): { conversationId: string; lastReadAt: string } {
    const user = this.staff();
    const row = this.find(user.tenantId, id);
    const now = new Date().toISOString();
    this.reads.set(`${row.id}:${user.id}`, now);
    return { conversationId: row.id, lastReadAt: now };
  }

  listUnmatched(query: { page?: number; limit?: number } = {}): types.ConversationPage<types.UnmatchedConversation> {
    const user = this.staff();
    const page = query.page ?? 1;
    const limit = query.limit ?? 25;
    const rows = this.rows
      .filter((r) => r.tenantId === user.tenantId && r.status === 'UNMATCHED' && !r.discardedAt)
      .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt))
      .map((r) => {
        const messages = this.messages.filter((m) => m.conversationId === r.id).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        return { ...this.view(r, user.id), unread: false, from: messages[0]?.fromAddress ?? null, preview: (messages[0]?.displayText ?? '').slice(0, 200), messageCount: messages.length };
      });
    return { data: rows.slice((page - 1) * limit, page * limit), pagination: { page, limit, total: rows.length, pages: Math.max(1, Math.ceil(rows.length / limit)) } };
  }

  assignCustomer(id: string, data: types.AssignCustomerInput): types.Conversation {
    const user = this.staff();
    const row = this.find(user.tenantId, id);
    const hasCustomer = 'customerId' in data && !!data.customerId;
    const hasConversation = 'conversationId' in data && !!data.conversationId;
    if (hasCustomer === hasConversation) throw invalid('Send either customerId or conversationId');
    if ('customerId' in data && data.customerId && !this.host.customer(user.tenantId, data.customerId)) throw notFound('Customer not found');
    if (row.status !== 'UNMATCHED' || row.discardedAt) throw conflict('Only unmatched mail can be attached or discarded', 'CONVERSATION_NOT_UNMATCHED');
    if ('customerId' in data && data.customerId) {
      const customer = this.host.customer(user.tenantId, data.customerId)!;
      Object.assign(row, { customer: { id: customer.id, name: customer.name, email: customer.email ?? null }, status: 'OPEN', closedAt: null, heldForConversationId: null });
      return this.view(row, user.id);
    }
    const targetId = (data as { conversationId: string }).conversationId;
    if (targetId === row.id) throw invalid('Choose another conversation');
    const target = this.rows.find((r) => r.id === targetId && r.tenantId === user.tenantId && !r.discardedAt);
    if (!target) throw notFound();
    if (target.status === 'UNMATCHED') throw conflict('Merge into a conversation that has a customer', 'CONVERSATION_UNMATCHED');
    this.messages.filter((m) => m.conversationId === row.id).forEach((m) => { m.conversationId = target.id; });
    if (row.lastInboundAt && (!target.lastInboundAt || row.lastInboundAt > target.lastInboundAt)) target.lastInboundAt = row.lastInboundAt;
    if (row.lastMessageAt > target.lastMessageAt) target.lastMessageAt = row.lastMessageAt;
    Object.assign(target, { status: 'OPEN', closedAt: null });
    row.discardedAt = new Date().toISOString();
    return this.view(target, user.id);
  }

  discard(id: string): types.Conversation {
    const user = this.staff();
    const row = this.find(user.tenantId, id);
    if (row.status !== 'UNMATCHED' || row.discardedAt) throw conflict('Only unmatched mail can be attached or discarded', 'CONVERSATION_NOT_UNMATCHED');
    row.discardedAt = new Date().toISOString();
    return this.view(row, user.id);
  }

  // ----------------------------------------------------------------- portal

  private portalThread(scope: { tenantId: string; customerId: string; userId: string }): Row | undefined {
    return this.rows.find((r) => r.tenantId === scope.tenantId && r.portalThreadKey === `${scope.userId}:${scope.customerId}`);
  }

  private portalView(row: Row | undefined, userId: string): types.PortalThread {
    if (!row) return { conversationId: null, messages: [], unreadCount: 0 };
    const lastRead = this.lastRead(row.id, userId);
    const visible = this.messages
      .filter((m) => m.conversationId === row.id && m.direction !== 'NOTE' && !m.isAutoReply)
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return {
      conversationId: row.id,
      messages: visible.map((m) => ({
        id: m.id,
        from: m.direction === 'OUTBOUND' ? 'STAFF' : 'CUSTOMER',
        body: m.displayText,
        authorName: m.direction === 'OUTBOUND' && m.authorUserId ? this.host.user(row.tenantId, m.authorUserId)?.firstName ?? null : null,
        createdAt: m.createdAt,
      })),
      unreadCount: visible.filter((m) => m.direction === 'OUTBOUND' && (!lastRead || m.createdAt > lastRead)).length,
    };
  }

  portalMessages(): types.PortalThread {
    const scope = this.host.portalCustomer();
    return this.portalView(this.portalThread(scope), scope.userId);
  }

  sendPortalMessage(rawBody: string): types.PortalThread {
    const scope = this.host.portalCustomer();
    const body = this.bodyOf(rawBody, 5000);
    const now = new Date().toISOString();
    const row = this.portalThread(scope)
      ?? this.newRow(scope.tenantId, { customerId: scope.customerId, subject: 'Message from the customer portal', createdAt: now, portalUserId: scope.userId });
    this.addInbound(row, scope.email, body, now, 'PORTAL', scope.userId);
    this.reads.set(`${row.id}:${scope.userId}`, now);
    return this.portalView(row, scope.userId);
  }

  markPortalRead(): { conversationId: string | null; unreadCount: number } {
    const scope = this.host.portalCustomer();
    const row = this.portalThread(scope);
    if (!row) return { conversationId: null, unreadCount: 0 };
    this.reads.set(`${row.id}:${scope.userId}`, new Date().toISOString());
    return { conversationId: row.id, unreadCount: 0 };
  }
}
